<template>
  <div class="profile">

    <!-- Header -->
    <div class="profile-line">
      <h1 class="profile-line-title"> {{ $t('Personal information') }} </h1>
      <a href="#" class="profile-line-link" @click.prevent="edit" v-if="!isEdited">
        <span class="profile-line-link-text"> {{ $t('Edit your profile') }} </span>
        <i class="material-icons base-icon profile-line-link-icon">edit</i>
      </a>
      <a href="#" class="profile-line-link" @click="exitSection" v-else> {{ $t('Cancel') }} </a>
    </div>
    <!---->

    <!-- Personal information block -->
    <section class="profile-block" v-if="!isEdited">
      <p> {{ currentUser.firstname }} {{ currentUser.lastname }} </p>
      <p> {{ currentUser.email }} </p>
      <p class="phone-verification" v-if="currentUser.telephone"> {{ currentUser.telephone }}
        <template v-if="!currentUser.phone_verified">
          <span class="verified-phone">
            <span class="material-icons base-icon warning-icon">error_outline</span>
            {{ $t('Your phone number is not verified') }}.
          </span>
          <span class="pointer cl-korzyna-green bold link ml10" @click="openVerifyModal">{{ $t('Verify') }}</span>
        </template>
      </p>
      <h3 v-else class="payment-block-telephone-title"> {{ $t('Please provide your phone number') }} </h3>
    </section>
    <!---->

    <!-- Edit information block -->
    <section class="profile-block" v-else>

      <!-- Email field -->
      <base-input class="col-xs-12 mb10 input profile-block-field"
                  type="email"
                  name="email-address"
                  autocomplete="email"
                  :placeholder="$t('Email address')"
                  v-model="currentUser.email"
                  :validations="[
                    {
                      condition: !$v.currentUser.email.required,
                      text: $t('Field is required')
                    },
                    {
                      condition: !$v.currentUser.email.email,
                      text: $t('Please provide valid e-mail address.')
                    }
                  ]"
      />
      <!---->

      <!-- Firstname field -->
      <base-input class="col-xs-12 mb10 input profile-block-field"
                  type="text"
                  name="first-name"
                  autocomplete="given-name"
                  :placeholder="$t('First name')"
                  v-model.trim="currentUser.firstname"
                  :maxlength="25"
                  @input="$v.currentUser.firstname.$touch()"
                  :validations="[
                    {
                      condition: !$v.currentUser.firstname.required,
                      text: $t('Field is required')
                    },
                    {
                      condition: !$v.currentUser.firstname.minLength,
                      text: $t('Name must have at least 2 letters.')
                    }
                  ]"
      />
      <!---->

      <!-- Lastname field -->
      <base-input class="col-xs-12 mb10 input profile-block-field"
                  type="text"
                  name="last-name"
                  autocomplete="family-name"
                  :placeholder="$t('Last name')"
                  v-model.trim="currentUser.lastname"
                  :maxlength="25"
                  @input="$v.currentUser.lastname.$touch()"
                  :validations="[{
                    condition: !$v.currentUser.lastname.required,
                    text: $t('Field is required')
                  }]"
      />
      <!---->

      <!-- Phone number field -->
      <base-telephone-input
        :required="true"
        :validations="phoneValidations"
        :value="currentUser.telephone"
        @update="updatePhoneNumber"
        @input="$v.currentUser.telephone.$touch()"
      />
      <!---->

      <!-- Accept button -->
      <div class="profile-block-actions">
        <button-full class="profile-block-actions-btn" :disabled="checkValidation()" @click.native="checkAndConfirmUpdate">
          {{ $t('Confirm changes') }}
        </button-full>
      </div>
      <!---->

    </section>
    <!---->

    <verify-phone-number-modal v-if="!currentUser.phone_verified" :phone="currentUser.telephone" @closeVerifyPhoneModal="closeVerifyModal"/>

    <confirm-phone-change-modal @closeConfirmPhoneModal="confirmUpdate"/>
  </div>
</template>

<script async>
import { required, minLength, email } from 'vuelidate/lib/validators'
import MyProfile from '@vue-storefront/core/compatibility/components/blocks/MyAccount/MyProfile'

const BaseInput = () => import(/* webpackChunkName: "vsf-base-input" */ 'theme/components/core/blocks/Form/BaseInput');
const BaseTelephoneInput = () => import(/* webpackChunkName: "vsf-base-telephone-input" */ 'theme/components/core/blocks/Form/BaseTelephoneInput');
const ButtonFull = () => import(/* webpackChunkName: "vsf-button-full" */ 'theme/components/theme/ButtonFull');
const VerifyPhoneNumberModal = () => import(/* webpackChunkName: "vsf-verify-phone-number-modal" */ 'theme/components/core/blocks/MyAccount/VerifyPhoneNumberModal');
const ConfirmPhoneChangeModal = () => import(/* webpackChunkName: "vsf-confirm-phone-modal" */ 'theme/components/core/blocks/MyAccount/ConfirmPhoneChangeModal');

export default {
  components: {
    BaseInput,
    ButtonFull,
    VerifyPhoneNumberModal,
    ConfirmPhoneChangeModal,
    BaseTelephoneInput
  },
  mixins: [MyProfile],
  data: () => ({
    phoneMeta: {
      isValid: false
    }
  }),
  computed: {
    phoneValidations () {
      return [
        {
          condition: this.$v.currentUser.telephone.$error && !this.$v.currentUser.telephone.required,
          text: this.$t('Field is required')
        },
        {
          condition: this.$v.currentUser.telephone.$error && this.$v.currentUser.telephone.required && !this.$v.currentUser.telephone.isValid,
          text: this.$t('Incorrect type of number')
        }
      ]
    }
  },
  methods: {
    checkValidation () {
      if (this.changePassword && this.addCompany) {
        return this.$v.$invalid
      } else if (this.changePassword && !this.addCompany) {
        return this.$v.currentUser.$invalid
      } else if (!this.changePassword && this.addCompany) {
        return this.$v.currentUser.$invalid || this.$v.userCompany.$invalid
      } else {
        return this.$v.currentUser.$invalid
      }
    },
    openVerifyModal () {
      this.$bus.$emit('request-verify-phone-number')
      this.$bus.$emit('modal-show', 'verify-phone-number')
    },
    closeVerifyModal (isVerify, modalName) {
      this.$bus.$emit('modal-hide', modalName)
      this.currentUser.phone_verified = isVerify
    },
    updatePhoneNumber (value) {
      this.phoneMeta = value;

      if ('formattedNumber' in value) {
        this.currentUser.telephone = value.formattedNumber;
      }
    },
    checkAndConfirmUpdate () {
      this.$store.state.user.current.telephone && this.$store.state.user.current.telephone !== this.currentUser.telephone ? this.$bus.$emit('modal-show', 'confirm-phone-change') : this.confirmUpdate()
    },
    confirmUpdate () {
      this.$v.currentUser.$touch();

      if (this.$v.currentUser.$invalid) {
        this.$store.dispatch('notification/spawnNotification', {
          type: 'error',
          message: this.$t('Some of the form fields are not valid!'),
          action1: { label: this.$t('OK') }
        })
        return;
      }
      this.updateProfile();
      if (!this.currentUser.phone_verified) {
        this.$store.dispatch('ipay-payment-user/clearCardsList')
      }
    }
  },
  mounted () {
    this.$bus.$on('clear-user-date', () => {
      this.currentUser = Object.assign({}, this.$store.state.user.current)
    })
  },
  validations () {
    return {
      currentUser: {
        firstname: {
          required,
          minLength: minLength(2)
        },
        lastname: {
          required
        },
        email: {
          required,
          email
        },
        telephone: {
          required: () => required && 'phoneNumber' in this.phoneMeta,
          isValid: () => this.phoneMeta.isValid
        }
      },
      userCompany: {
        company: {
          required
        },
        country: {
          required
        },
        street: {
          required
        },
        house: {
          required
        },
        postcode: {
          required,
          minLength: minLength(3)
        },
        city: {
          required
        },
        taxId: {
          required,
          minLength: minLength(3)
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.warning-icon {
  width: 26px;
  height: 26px;
  color: #ff0000;
}
  .profile {
    display: flex;
    flex-direction: column;

    @media only screen and (max-width: 576px) {
      padding: 0 20px;
    }

    &-line {
      height: 60px;
      margin-bottom: 20px;

      display: flex;
      justify-content: space-between;
      align-items: center;

      @media only screen and (max-width: 768px) {
        flex-direction: column;
        justify-content: center;
        align-items: flex-start;
      }

      @media only screen and (max-width: 576px) {
        flex-direction: row;
        justify-content: space-between;
        align-items: center;
      }

      &-title {
        margin: 0;

        color: #111;
        font-size: 20px;
        font-weight: 600;

        @media only screen and (max-width: 576px) {
          display: none;
        }
      }

      &-link {
        display: inline-flex;
        justify-content: space-between;
        align-items: center;

        @media only screen and (max-width: 576px) {
          font-size: 14px;
        }

        &-text {

        }

        &-icon {
          padding-left: 10px;
        }
      }
    }

    &-block {

      &-actions {
        display: flex;
        justify-content: center;

        &-btn {
          width: auto;
          padding: 16px 84px;

          font-size: 16px;
          font-weight: 400;
          border-radius: 5px;
        }
      }

      &-title {
        margin-bottom: 20px;

        color: #111;
        font-size: 16px;
        font-weight: 400;
        text-align: center;
      }

      &-field {
        padding: 0;
      }
    }
  }
  .payment-block-telephone-title {
    padding-left: 24px;
    color: #e02124;
    font-size: 14px;
    font-weight: 400;
    background-position: left center;
    background-size: 15px;
    background-repeat: no-repeat;
    background-image: url('../../../../assets/account/icon-attention.png');
  }
  .verified-phone {
    display: flex;
    align-items: center;
    margin-left: 10px;
    .warning-icon {
      margin-right: 5px;
    }
  }
  .link {
    text-decoration: underline;
    align-self: center;
  }
  .phone-verification {
    align-items: center;
    flex-wrap: wrap;
    display: flex;
  }
</style>
